import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Phone, Mail, MessageCircle, CheckCircle2, Save, History, Clock, User, AlertTriangle } from "lucide-react";

const STATUSES = ["Nuova", "Da contattare", "Contatto programmato", "In lavorazione", "Cliente contattato", "Interessato", "Documentazione richiesta", "Pagamento ricevuto", "Attivazione in corso", "Attivato", "Non interessato", "Irreperibile", "Annullato"];

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [req, setReq] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");
  const [fields, setFields] = useState({});

  const load = useCallback(async () => {
    const r = await api.get(`/admin/requests/${id}`);
    setReq(r.data);
    setNewStatus(r.data.status);
    setFields({
      assigned_operator: r.data.assigned_operator || "",
      outcome: r.data.outcome || "",
      internal_notes: r.data.internal_notes || "",
      activation_date: r.data.activation_date || "",
      amount_paid: r.data.amount_paid || "",
      payment_method: r.data.payment_method || "",
      mypos_code: r.data.mypos_code || "",
    });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async () => {
    await api.post(`/admin/requests/${id}/status`, { status: newStatus, note: statusNote });
    toast.success("Stato aggiornato");
    setStatusNote("");
    load();
  };

  const saveFields = async () => {
    const payload = { ...fields, amount_paid: fields.amount_paid ? parseFloat(fields.amount_paid) : null };
    await api.patch(`/admin/requests/${id}`, payload);
    toast.success("Modifiche salvate");
    load();
  };

  const logContact = async (channel) => {
    await api.post(`/admin/requests/${id}/contact`, { channel, note: `Contatto via ${channel}` });
    toast.success("Contatto registrato");
    load();
  };

  if (!req) return <div className="text-gray-400">Caricamento…</div>;

  const cleanPhone = (req.phone || "").replace(/\s/g, "");

  return (
    <div className="space-y-6 max-w-5xl">
      <button onClick={() => navigate("/admin/richieste")} data-testid="back-btn" className="flex items-center gap-1 text-sm text-gray-500 hover:text-black">
        <ArrowLeft className="w-4 h-4" /> Torna alle richieste
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight font-mono">{req.code}</h1>
            {req.priority === "high" && <Badge className="bg-red-100 text-red-700 border-0">Priorità alta</Badge>}
            {req.is_duplicate && <Badge className="bg-amber-100 text-amber-700 border-0 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Possibile duplicato</Badge>}
          </div>
          <p className="text-gray-500 mt-1">{req.company_name}</p>
        </div>
        <Badge className="bg-yellow-50 text-yellow-800 border border-yellow-200 text-sm px-3 py-1">{req.status}</Badge>
      </div>

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2">
        <Button asChild variant="outline" className="rounded-xl" data-testid="action-call"><a href={`tel:${cleanPhone}`} onClick={() => logContact("call")}><Phone className="w-4 h-4 mr-1.5" /> Chiama</a></Button>
        <Button asChild variant="outline" className="rounded-xl" data-testid="action-email"><a href={`mailto:${req.email}`} onClick={() => logContact("email")}><Mail className="w-4 h-4 mr-1.5" /> Email</a></Button>
        <Button asChild variant="outline" className="rounded-xl" data-testid="action-whatsapp"><a href={`https://wa.me/${cleanPhone.replace("+", "")}`} target="_blank" rel="noreferrer" onClick={() => logContact("whatsapp")}><MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp</a></Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Client data */}
          <Card title="Dati cliente" icon={User}>
            <Grid data={[
              ["Ragione sociale", req.company_name], ["Referente", req.contact_name],
              ["Telefono", req.phone], ["Email", req.email],
              ["Partita IVA", req.vat || "—"], ["Comune / Provincia", req.location || "—"],
            ]} />
          </Card>

          {/* Agent data */}
          <Card title="Dati agente e segnalazione">
            <Grid data={[
              ["Agente", req.agent_name], ["Codice agente", req.agent_code || "—"],
              ["Telefono agente", req.agent_phone || "—"], ["Email agente", req.agent_email || "—"],
              ["Modalità contatto", req.contact_mode === "immediate" ? "Contatto immediato" : "Contatto programmato"],
              ["Appuntamento", req.preferred_date ? `${req.preferred_date} · ${req.preferred_time_slot}` : "—"],
              ["Inserita il", (req.created_at || "").replace("T", " ").slice(0, 16)],
              ["Consenso privacy", req.privacy_consent ? "Sì" : "No"],
            ]} />
            {req.notes && <div className="mt-4 text-sm"><span className="text-gray-500">Note iniziali:</span> {req.notes}</div>}
            {req.duplicate_of && <div className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">Collegata a segnalazione precedente: <b>{req.duplicate_of}</b></div>}
          </Card>

          {/* Editable fields */}
          <Card title="Gestione operativa">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Operatore assegnato"><Input data-testid="field-operator" value={fields.assigned_operator} onChange={(e) => setFields({ ...fields, assigned_operator: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Esito"><Input data-testid="field-outcome" value={fields.outcome} onChange={(e) => setFields({ ...fields, outcome: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Data attivazione"><Input type="date" value={fields.activation_date} onChange={(e) => setFields({ ...fields, activation_date: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Importo pagato (€)"><Input type="number" value={fields.amount_paid} onChange={(e) => setFields({ ...fields, amount_paid: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Metodo di pagamento"><Input value={fields.payment_method} onChange={(e) => setFields({ ...fields, payment_method: e.target.value })} className="rounded-xl" /></Field>
              <Field label="Codice myPOS cliente"><Input value={fields.mypos_code} onChange={(e) => setFields({ ...fields, mypos_code: e.target.value })} className="rounded-xl" /></Field>
              <div className="md:col-span-2"><Field label="Note interne"><Textarea data-testid="field-internal-notes" rows={3} value={fields.internal_notes} onChange={(e) => setFields({ ...fields, internal_notes: e.target.value })} className="rounded-xl" /></Field></div>
            </div>
            <Button onClick={saveFields} data-testid="save-fields-btn" className="mt-4 rounded-xl bg-[#111111] hover:bg-black text-white"><Save className="w-4 h-4 mr-1.5" /> Salva modifiche</Button>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <Card title="Cambia stato">
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="status-select" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Textarea placeholder="Nota (facoltativa)" rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} className="mt-3 rounded-xl" />
            <Button onClick={changeStatus} data-testid="change-status-btn" className="mt-3 w-full rounded-xl bg-[#FFCC00] hover:bg-[#E6B800] text-black font-semibold"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Aggiorna stato</Button>
          </Card>

          <Card title="Storico modifiche" icon={History}>
            <div className="space-y-3">
              {(req.status_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-sm border-l-2 border-[#FFCC00] pl-3">
                  <p className="font-medium">{h.status}</p>
                  <p className="text-xs text-gray-500 flex items-center gap-1"><Clock className="w-3 h-3" />{(h.at || "").replace("T", " ").slice(0, 16)} · {h.by}</p>
                  {h.note && <p className="text-xs text-gray-600 mt-0.5">{h.note}</p>}
                </div>
              ))}
            </div>
          </Card>

          {(req.contact_history || []).length > 0 && (
            <Card title="Storico contatti">
              <div className="space-y-2">
                {req.contact_history.slice().reverse().map((c, i) => (
                  <div key={i} className="text-sm text-gray-600">{(c.at || "").slice(0, 16).replace("T", " ")} · {c.channel} · {c.by}</div>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
      <h2 className="font-display font-semibold mb-4 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-[#E6B800]" />}{title}</h2>
      {children}
    </div>
  );
}
function Grid({ data }) {
  return (
    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
      {data.map(([k, v]) => (
        <div key={k}><p className="text-gray-500 text-xs">{k}</p><p className="font-medium break-words">{v}</p></div>
      ))}
    </div>
  );
}
function Field({ label, children }) {
  return <div><Label className="mb-1.5 block text-xs text-gray-500">{label}</Label>{children}</div>;
}
